<template>
  <section class="container grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8 mt-6">
    <div class="col-span-2 bg-white border p-6 rounded-xl mb-8 lg:mb-8">
      <PlanAlert v-if="this.current_plan === 'lite' && this.boards_length >= 1" :title="$t('Upgrade your plan to add more boards')" :description="$t('The Lite plan is limited to a single board. Please upgrade your plan to add more.')" :link="'/edit?view=billing'" :click="$t('Click here to upgrade')" class="mb-6"/>
      <div class="flex items-center justify-between">
        <h2
          class="font-semibold text-2xl text-neutral-800"
          v-if="this.originalBoard.name === null"
        >
          {{ $t('New feedback board') }}
        </h2>
        <h2 class="font-semibold text-2xl text-neutral-800" v-else>
          {{ $t('Board settings') }}
        </h2>
        <delete-board-dialog
          v-if="this.allBoards && this.allBoards.length >= 1"
          :original-board="this.originalBoard"
          :all-boards="this.allBoards"
        >
        </delete-board-dialog>
      </div>

      <div class="error-box" v-if="this.errors && this.errors.length > 0">
        <p>{{ $t('These errors prevented the board from saving:') }}</p>
        <ul>
          <li v-for="error in this.errors" :key="error">{{ error }}</li>
        </ul>
      </div>

      <form
        :action="this.current_plan === 'lite' && this.boards_length >= 0 ? '#' : this.form_action"
        accept-charset="UTF-8"
        method="post"
        enctype="multipart/form-data"
        class="mt-4"
      >
        <input
          type="hidden"
          name="authenticity_token"
          :value="this.form_token"
        />
        <input name="_method" type="hidden" :value="this.form_method" />

        <h3 class="font-semibold text-lg text-neutral-800">{{ $t('General') }}</h3>

        <div class="mt-2">
          <label class="label inline-block">{{ $t('Name') }}</label>
          <small
            class="text-neutral-700 text-sm float-right"
            :class="{
              'text-red-500': this.name && this.name.length > 30,
            }"
            >{{ this.name ? this.name.length : "0" }}/30</small
          >
          <input
            id="board_name"
            type="text"
            name="board[name]"
            class="input"
            v-model="name"
            :placeholder="$t('i.e. Feedback and ideas')"
            maxlength="30"
            autofocus
            required
          />
        </div>
        <div class="mt-4">
          <label class="label inline-block">{{ $t('Slug') }}</label>
          <small class="text-neutral-700 text-sm float-right"
            >{{ $t('Use only a-z, 0-9 and "-"') }}</small
          >
          <div class="flex items-center gap-1 input">
            <span class="flex-shrink-0 text-neutral-700 hidden md:block" id="span-id"
              >https://{{ this.project_subdomain }}.feedbear.com/boards/</span
            >
            <input
              id="board_slug"
              type="text"
              name="board[slug]"
              v-model="slug"
              class="flex-grow outline-none"
              :placeholder="$t('feedback-and-ideas')"
              pattern="[a-z0-9\-]+"
              required
            />
          </div>
        </div>
        <div class="mt-4">
          <label class="label inline-block">{{ $t('Page heading') }}</label>
          <small
            class="text-neutral-700 text-sm float-right"
            :class="{
              'text-red-500': this.page_heading && this.page_heading.length > 100,
            }"
            >{{ this.page_heading ? this.page_heading.length : "0" }}/100</small
          >
          <div class="flex items-center flex-wrap">
            <input
              id="page_heading"
              type="text"
              name="board[page_heading]"
              class="input"
              v-model="page_heading"
              maxlength="100"
            />
          </div>
        </div>

        <div class="mt-4">
          <label class="label inline-block">{{ $t('Page description') }}</label>
          <RichTextEditor
            input-id="page_description"
            input-name="board[page_description]"
            :value="page_description"
            variant="description"
            :enable-mentions="false"
            :enable-image-upload="true"
            :enable-attachments="false"
            :enable-embeds="false"
            :enable-drafts="false"
            @input="page_description = $event"
          />
        </div>

        <div class="relative mt-4">
          <ColorPicker
            :current-color="heading_font_color"
            input-name="board[heading_font_color]"
            :label="$t('Header text color')"
            v-model="heading_font_color"
          />
        </div>

        <div class="mt-4">
          <ImageUploader
            :imageLable="$t('Header image')"
            :image="header_image_preview || header_image"
            :removeImage="remove_header_image"
            @deleteImg="deleteHeaderImage"
            id="header_image"
            @changeImg="changeHeaderImage"
            :buttonName="$t('Upload header image')"
            name="board[header_image]"
          />
          <input
            type="hidden"
            name="board[remove_header_image]"
            id="deleteHeaderImage"
            value="off"
          />
        </div>

        <div class="mt-4">
          <GradientPicker
            :bg-type="header_bg_type"
            :bg-value="header_bg_value"
            input-name-type="board[header_bg_type]"
            input-name-value="board[header_bg_value]"
          />
        </div>

        <h3 class="mt-8 text-lg font-semibold text-neutral-800">
          {{ $t('New idea form customization') }}
        </h3>

        <div class="mt-2">
          <label class="label inline-block">{{ $t('Intro heading') }}</label>
          <small
            class="text-neutral-700 text-sm float-right"
            :class="{
              'text-red-500':
                this.intro_heading && this.intro_heading.length > 30,
            }"
            >{{
              this.intro_heading ? this.intro_heading.length : "0"
            }}/30</small
          >
          <input
            id="board_intro_heading"
            type="text"
            name="board[intro_heading]"
            class="input"
            v-model="intro_heading"
            maxlength="30"
            required
          />
        </div>
        <div class="mt-4">
          <label class="label inline-block">{{ $t('Intro text') }}</label>
          <small
            class="text-neutral-700 text-sm float-right"
            :class="{
              'text-red-500': this.intro_text && this.intro_text.length > 300,
            }"
            >{{ this.intro_text ? this.intro_text.length : "0" }}/300</small
          >
          <textarea
            id="board_intro_text"
            rows="3"
            name="board[intro_text]"
            class="input"
            v-model="intro_text"
            maxlength="300"
            required
          ></textarea>
        </div>
        <div class="mt-4">
          <label class="label inline-block">{{ $t('Form title') }}</label>
          <small
            class="text-neutral-700 text-sm float-right"
            :class="{
              'text-red-500': this.form_title && this.form_title.length > 30,
            }"
            >{{ this.form_title ? this.form_title.length : "0" }}/30</small
          >
          <input
            id="board_form_title"
            type="text"
            name="board[form_title]"
            class="input"
            v-model="form_title"
            maxlength="30"
            required
          />
        </div>
        <div class="mt-4">
          <label class="label inline-block">{{ $t('Form title placeholder') }}</label>
          <small
            class="text-neutral-700 text-sm float-right"
            :class="{
              'text-red-500':
                this.form_title_placeholder &&
                this.form_title_placeholder.length > 30,
            }"
            >{{
              this.form_title_placeholder
                ? this.form_title_placeholder.length
                : "0"
            }}/30</small
          >
          <input
            id="board_form_title_placeholder"
            type="text"
            name="board[form_title_placeholder]"
            class="input"
            v-model="form_title_placeholder"
            maxlength="30"
            required
          />
        </div>
        <div class="mt-4">
          <label class="label inline-block">{{ $t('Form body') }}</label>
          <small
            class="text-neutral-700 text-sm float-right"
            :class="{
              'text-red-500':
                this.form_content && this.form_content.length > 30,
            }"
            >{{ this.form_content ? this.form_content.length : "0" }}/30</small
          >
          <input
            id="board_form_content"
            type="text"
            name="board[form_content]"
            class="input"
            v-model="form_content"
            maxlength="30"
            required
          />
        </div>
        <div class="mt-4">
          <label class="label inline-block">{{ $t('Form body placeholder') }}</label>
          <small
            class="text-neutral-700 text-sm float-right"
            :class="{
              'text-red-500':
                this.form_content_placeholder &&
                this.form_content_placeholder.length > 60,
            }"
            >{{
              this.form_content_placeholder
                ? this.form_content_placeholder.length
                : "0"
            }}/60</small
          >
          <input
            id="board_form_content_placeholder"
            type="text"
            name="board[form_content_placeholder]"
            class="input"
            v-model="form_content_placeholder"
            maxlength="60"
            required
          />
        </div>

        <div class="mt-4">
          <label class="label inline-block">{{ $t('Form footer text') }}</label>
          <small
            class="text-neutral-700 text-sm float-right"
            :class="{
              'text-red-500':
                this.form_footer_text && this.form_footer_text.length > 500,
            }"
            >{{
              this.form_footer_text ? this.form_footer_text.length : "0"
            }}</small
          >
          <textarea
            id="board_form_footer_text"
            name="board[form_footer_text]"
            class="input"
            v-model="form_footer_text"
          />
        </div>

        <div class="mt-4">
          <label class="label inline-block">{{ $t('Form button text') }}</label>
          <small
            class="text-neutral-700 text-sm float-right"
            :class="{
              'text-red-500':
                this.form_button_text && this.form_button_text.length > 20,
            }"
            >{{
              this.form_button_text ? this.form_button_text.length : "0"
            }}/20</small
          >
          <input
            id="board_form_button_text"
            type="text"
            name="board[form_button_text]"
            class="input"
            v-model="form_button_text"
            maxlength="20"
            required
          />
        </div>


        <div class="mt-8">
          <h3 class="text-lg font-semibold text-neutral-800">{{ $t('Status settings') }}</h3>
          <p class="settings-section__desc">{{ $t('Configure how statuses work on this board.') }}</p>
          <div class="settings-card">
            <div class="settings-card__row">
              <div class="settings-card__row-content">
                <span class="settings-card__row-title">{{ $t('Default status for new ideas') }}</span>
                <span class="settings-card__row-desc mb-2">{{ $t('When new ideas are submitted, assign them this status') }}</span>
              </div>
              <div class="settings-card__row-action" v-if="selectedStatus">
                <select
                  id="status"
                  name="board[status_id]"
                  v-model="selectedValue"
                  class="settings-card__select"
                >
                  <option v-for="option in allStatuses" :key="option.id" :value="option.id">{{ option.name }}</option>
                </select>
              </div>
            </div>
            <div class="settings-card__row">
              <label class="settings-card__toggle">
                <div class="button--toggle">
                  <input type="checkbox" class="checkbox" v-model="hide_not_set" name="board[hide_not_set_status]" />
                  <div class="knobs"></div>
                  <div class="layer"></div>
                </div>
                <div class="settings-card__row-content">
                  <span class="settings-card__row-title">{{ $t('Hide ideas with "Not Set" status from users') }}</span>
                  <span class="settings-card__row-desc">{{ $t('Only admins will be able to see and filter ideas marked with the "Not Set" status.') }}</span>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div class="mt-8">
          <h3 class="text-lg font-semibold text-neutral-800">{{ $t('Sorting settings') }}</h3>
          <p class="settings-section__desc">{{ $t('Choose how ideas are ordered on this board.') }}</p>
          <div class="settings-card">
            <div class="settings-card__row">
              <div class="settings-card__row-content">
                <span class="settings-card__row-title">{{ $t('Default sort order') }}</span>
                <span class="settings-card__row-desc mb-2">{{ $t('Visitors see ideas in this order first. They can still switch to another sort.') }}</span>
              </div>
              <div class="settings-card__row-action">
                <select
                  id="board_default_sort"
                  name="board[default_sort]"
                  v-model="default_sort"
                  class="settings-card__select"
                >
                  <option v-for="option in sortOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-8">
          <h3 class="text-lg font-semibold text-neutral-800">{{ $t('Voting settings') }}</h3>
          <p class="settings-section__desc">{{ $t('Control how votes are displayed.') }}</p>
          <div class="settings-card">
            <div class="settings-card__row">
              <label class="settings-card__toggle">
                <div class="button--toggle">
                  <input type="checkbox" class="checkbox" v-model="allow_vote_count" name="board[allow_vote_count]" />
                  <div class="knobs"></div>
                  <div class="layer"></div>
                </div>
                <div class="settings-card__row-content">
                  <span class="settings-card__row-title">{{ $t('Hide count of votes') }}</span>
                  <span class="settings-card__row-desc">{{ $t('The number of votes on ideas will not be displayed to users') }}</span>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div class="mt-8">
          <h3 class="text-lg font-semibold text-neutral-800">{{ $t('Privacy settings') }}</h3>
          <p class="settings-section__desc">{{ $t('Control who can see this board.') }}</p>
          <div class="settings-card">
            <div class="settings-card__row">
              <label class="settings-card__toggle">
                <div class="button--toggle">
                  <input type="checkbox" class="checkbox" v-model="is_private" name="board[is_private]" />
                  <div class="knobs"></div>
                  <div class="layer"></div>
                </div>
                <div class="settings-card__row-content">
                  <span class="settings-card__row-title">{{ $t('Set board as private') }}</span>
                  <span class="settings-card__row-desc">{{ $t('This board will be hidden from the public and only accessible to admins of this project.') }}</span>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div class="mt-8">
          <h3 class="text-lg font-semibold text-neutral-800">{{ $t('Tag Management') }}</h3>
          <p class="text-sm text-neutral-600 mt-1 mb-3">
            {{ $t('Manage tags for this board. Deleting a tag will remove it from all associated posts.') }}
          </p>
          <tag-management
            v-if="this.originalBoard.slug"
            :board-slug="this.originalBoard.slug"
          />
        </div>

        <div class="mt-6">
          <input
            type="submit"
            name="commit"
            :value="$t('Save board')"
            :disabled="this.current_plan === 'lite' && this.boards_length >= 1"
            class="btn-main"
          />
        </div>
      </form>
    </div>

    <div class="grid-span-1 flex flex-col items-center">
      <div
        class="w-full max-w-sm text-left text-neutral-700 font-bold uppercase tracking-wider text-sm flex items-center leading-none"
      >
        <div class="bg-red-500 w-2 h-2 rounded-full mr-2 animate-pulse"></div>
        <span>{{ $t('Live preview') }}</span>
      </div>
      <div class="bg-white rounded-xl border px-5 py-4 max-w-sm mt-2">
        <h4 class="text-lg font-bold text-dblue-500">
          {{ this.intro_heading }}
        </h4>

        <!-- prettier-ignore -->
        <p class="whitespace-pre-line text-sm mt-2 text-neutral-800">{{ this.intro_text }}
        </p>

        <form action="#" class="">
          <div class="mt-3">
            <label for="title" class="label inline-block">{{
              this.form_title
            }}</label>
            <input
              id="post_title"
              autocomplete="off"
              type="text"
              name="title"
              class="input"
              :placeholder="this.form_title_placeholder"
              value
              disabled
            />
          </div>

          <div class="mt-3">
            <label for="content" class="label inline-block">{{
              this.form_content
            }}</label>
            <textarea
              id="post_content"
              rows="4"
              required="required"
              name="content"
              class="input"
              :placeholder="this.form_content_placeholder"
              value
              disabled
            ></textarea>
          </div>

          <p class="whitespace-pre-line mt-2 text-neutral-800 text-left break-words"
          >{{this.form_footer_text}}</p>

          <div class="mt-3">
            <button
              type="button"
              class="btn-main w-full text-center block"
              disabled
            >
              {{ this.form_button_text }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script>
import PlanAlert from "../projects/settings/plan_alert"
import ImageUploader from "../components/image_uploader"
import ColorPicker from "../color-picker.vue"
import TagManagement from "./tag_management.vue"
import RichTextEditor from "../components/rich_text_editor/RichTextEditor.vue"
import GradientPicker from "../components/gradient_picker.vue"

export default {
  props: [
    "original-board",
    "original-project",
    "auth-token",
    "form-action",
    "form-method",
    "errors",
    "all-boards",
    "all-status",
    "selected-status",
    "status-name",
    "current-plan",
    "boards-length",
  ],
  data: function () {
    return {
      form_action: this.formAction,
      form_method: this.formMethod,
      form_token: this.authToken,
      project_subdomain: this.originalProject.subdomain,
      name: this.originalBoard.name,
      slug: this.originalBoard.slug,
      allow_vote_count: JSON.parse(this.originalBoard.allow_vote_count),
      is_private: JSON.parse(this.originalBoard.is_private),

      page_heading: this.originalBoard.page_heading,
      page_description: this.originalBoard.page_description,
      intro_heading: this.originalBoard.intro_heading || "Add your idea",
      intro_text:
        this.originalBoard.intro_text ||
        "We value your feedback. You can vote for existing ideas, discuss them or add your own.",
      form_title: this.originalBoard.form_title || "Title",
      form_title_placeholder:
        this.originalBoard.form_title_placeholder || "Something short",
      form_content: this.originalBoard.form_content || "Description",
      form_content_placeholder:
        this.originalBoard.form_content_placeholder ||
        "Write about your idea in more detail here",
      form_button_text: this.originalBoard.form_button_text || "Add idea",
      form_footer_text: this.originalBoard.form_footer_text,
      allStatuses: this.allStatus,
      selectedValue: "",
      default_sort: this.originalBoard.default_sort || "trend_score",
      hide_not_set: this.originalBoard.hide_not_set_status,
      current_plan: this.currentPlan,
      boards_length: this.boardsLength,
      header_image: this.originalBoard.header_image_url || '',
      header_image_preview: null,
      remove_header_image: 0,
      heading_font_color: this.originalBoard.heading_font_color || '#2E3F51',
      header_bg_type: this.originalBoard.header_bg_type || 'none',
      header_bg_value: this.originalBoard.header_bg_value || '',
    };
  },
  mounted() {
    this.selectedValue = this.selectedValuetSet()
  },
  components: {
    PlanAlert,
    ImageUploader,
    ColorPicker,
    TagManagement,
    RichTextEditor,
    GradientPicker,
  },
  computed: {
    // Same keys and labels as the sort dropdown in posts.vue
    sortOptions() {
      return [
        { value: "trend_score", label: this.$t("Trending") },
        { value: "total_upvotes", label: this.$t("Most votes") },
        { value: "created_at", label: this.$t("Recent") },
        { value: "comments_count", label: this.$t("Most discussed") },
      ];
    },
  },
  methods: {
    slugify: function (text) {
      text = text.toString().toLowerCase().trim();

      const sets = [
        { to: "a", from: "[ÀÁÂÃÄÅÆĀĂĄẠẢẤẦẨẪẬẮẰẲẴẶ]" },
        { to: "c", from: "[ÇĆĈČ]" },
        { to: "d", from: "[ÐĎĐÞ]" },
        { to: "e", from: "[ÈÉÊËĒĔĖĘĚẸẺẼẾỀỂỄỆ]" },
        { to: "g", from: "[ĜĞĢǴ]" },
        { to: "h", from: "[ĤḦ]" },
        { to: "i", from: "[ÌÍÎÏĨĪĮİỈỊ]" },
        { to: "j", from: "[Ĵ]" },
        { to: "ij", from: "[Ĳ]" },
        { to: "k", from: "[Ķ]" },
        { to: "l", from: "[ĹĻĽŁ]" },
        { to: "m", from: "[Ḿ]" },
        { to: "n", from: "[ÑŃŅŇ]" },
        { to: "o", from: "[ÒÓÔÕÖØŌŎŐỌỎỐỒỔỖỘỚỜỞỠỢǪǬƠ]" },
        { to: "oe", from: "[Œ]" },
        { to: "p", from: "[ṕ]" },
        { to: "r", from: "[ŔŖŘ]" },
        { to: "s", from: "[ßŚŜŞŠ]" },
        { to: "t", from: "[ŢŤ]" },
        { to: "u", from: "[ÙÚÛÜŨŪŬŮŰŲỤỦỨỪỬỮỰƯ]" },
        { to: "w", from: "[ẂŴẀẄ]" },
        { to: "x", from: "[ẍ]" },
        { to: "y", from: "[ÝŶŸỲỴỶỸ]" },
        { to: "z", from: "[ŹŻŽ]" },
        { to: "-", from: "[·/_,:;']" },
      ];

      sets.forEach((set) => {
        text = text.replace(new RegExp(set.from, "gi"), set.to);
      });

      text = text
        .toString()
        .toLowerCase()
        .replace(/\s+/g, "-") // Replace spaces with -
        .replace(/&/g, "-and-") // Replace & with 'and'
        .replace(/[^\w\-]+/g, "") // Remove all non-word chars
        .replace(/\--+/g, "-") // Replace multiple - with single -
        .replace(/^-+/, "") // Trim - from start of text
        .replace(/-+$/, ""); // Trim - from end of text

      return text;
    },
    selectedValuetSet() {
      let result = this.allStatuses.filter(status => status.id === (this.selectedStatus?.id || ''))
      if (result.length) return this.selectedStatus.id
      result = this.allStatuses.filter(status => status.name === this.statusName.not_set)
      return result[0].id;
    },
    changeHeaderImage(e) {
      const file = e.target.files[0];
      if (!file) {
        this.header_image = '';
        this.header_image_preview = null;
        return;
      }
      this.header_image = file;
      this.header_image_preview = URL.createObjectURL(file);
      document.getElementById('deleteHeaderImage').value = 'off';
    },
    deleteHeaderImage() {
      document.getElementById('header_image').value = '';
      document.getElementById('deleteHeaderImage').value = 'on';
      this.header_image = '';
      this.header_image_preview = null;
      this.remove_header_image = true;
    },
  },
  watch: {
    name: function (newName) {
      this.slug = this.slugify(newName);
    },
  },
};
</script>

<style scoped lang="scss">
.error-box {
  margin-top: 2rem;
  border: 1px solid red;
  padding: 1rem;
  border-radius: 4px;
}

.error-box li {
  font-weight: bold;
}

@media only screen and (max-width: 420px){
  #span-id{
    font-size: smaller;
  }
}

// Settings sections
.settings-section__desc {
  font-size: 13px;
  color: #737373;
  margin: 4px 0 12px;
}

.settings-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

.settings-card__row {
  padding: 14px 16px;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
}

.settings-card__toggle {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  margin: 0;
}

.settings-card__checkbox {
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid #d1d5db;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  position: relative;
  transition: all 0.15s ease;
  flex-shrink: 0;
  background: #fff;
  margin-top: 1px;

  &:checked {
    background: var(--accent-color, #009be5);
    border-color: var(--accent-color, #009be5);

    &::after {
      content: '';
      position: absolute;
      left: 5px;
      top: 1px;
      width: 6px;
      height: 10px;
      border: solid #fff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  &:hover {
    border-color: #9ca3af;
  }
}

.settings-card__row-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.settings-card__row-title {
  font-size: 13px;
  font-weight: 600;
  color: #404040;
}

.settings-card__row-desc {
  font-size: 12px;
  color: #737373;
  line-height: 1.4;
}

.settings-card__row-action {
  flex-shrink: 0;
  min-width: 160px;
}

.settings-card__select {
  width: 100%;
  padding: 8px 32px 8px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #404040;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  transition: all 0.15s ease;
  text-transform: capitalize;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath fill-rule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' clip-rule='evenodd'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;

  &:hover {
    border-color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: var(--accent-color, #009be5);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent-color, #009be5) 15%, transparent);
  }
}

/* House pill toggle (same pattern as the settings tabs; styles are scoped
   per component in this codebase) */
.settings-card__toggle {
  align-items: center;
}

.button--toggle {
  position: relative;
  width: 74px;
  height: 36px;
  overflow: hidden;
  flex-shrink: 0;
}

.button--toggle,
.button--toggle .layer {
  border-radius: 100px;
}

.button--toggle .checkbox {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 3;
}

.knobs,
.layer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.knobs {
  z-index: 2;
}

.layer {
  width: 100%;
  background-color: #f5f5f5;
  transition: 0.3s ease all;
  z-index: 1;
  border: 1px solid gainsboro;
}

.button--toggle .knobs:before {
  content: "";
  position: absolute;
  top: 4px;
  left: 4px;
  width: 28px;
  height: 28px;
  background-color: #d2d2d2;
  border-radius: 50%;
  transition: 0.3s ease all, left 0.3s cubic-bezier(0.18, 0.89, 0.35, 1.15);
}

.button--toggle .checkbox:checked + .knobs:before {
  content: "";
  left: 42px;
  background-color: #3bd087;
}

.button--toggle .checkbox:checked ~ .layer {
  background-color: #d1f0e1;
  border-color: #a3e6c6;
}
</style>
